import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { PageShell } from "@/components/layout/AppLayout";
import { GlassCard } from "@/components/glass/GlassCard";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, FloppyDisk, ArrowCounterClockwise, Lock, Users } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useRBAC } from "@/contexts/RBACContext";

const ROLE_LABELS = {
  admin: "Tenant Admin",
  contract_manager: "Contract Manager",
  manager: "Manager",
  signatory: "Signatory",
  viewer: "Viewer",
  global_admin: "Global Admin",
  clariva_support: "Clariva Support",
};
const ROLE_DESCRIPTIONS = {
  admin: "Full access to this tenant; manages users, billing and settings.",
  contract_manager: "Owns the contract lifecycle end-to-end.",
  manager: "Oversees a team's contracts and obligations.",
  signatory: "Reviews and signs contracts assigned to them.",
  viewer: "Read-only — cannot edit or sign.",
  global_admin: "Clariva staff — bypasses tenant RBAC.",
  clariva_support: "Clariva support — bypasses tenant RBAC via impersonation.",
};
const LOCKED_ROLES_FALLBACK = new Set(["global_admin", "clariva_support"]); // always bypass — matrix shown for info only

export default function RolesPermissions() {
  const { refresh: refreshRbac } = useRBAC();
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/permissions").then(r => {
      setData(r.data);
      setMatrix(r.data.matrix || {});
    }).catch(e => toast.error(e?.response?.data?.detail || "Failed to load permissions"));
  };
  useEffect(() => { load(); }, []);

  const LOCKED_ROLES = useMemo(
    () => new Set(data?.locked_roles && data.locked_roles.length ? data.locked_roles : LOCKED_ROLES_FALLBACK),
    [data]
  );

  const toggle = (role, modKey) => {
    if (LOCKED_ROLES.has(role)) return;
    // Admin self-lockout protection: enforce admin floor modules
    if (role === "admin" && (data?.admin_floor_modules || []).includes(modKey)) {
      toast.error("This module is required for the Admin role and cannot be removed.");
      return;
    }
    setMatrix(prev => {
      const cur = new Set(prev[role] || []);
      if (cur.has(modKey)) cur.delete(modKey); else cur.add(modKey);
      return { ...prev, [role]: Array.from(cur) };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/permissions", { permissions: matrix });
      toast.success("Permissions saved — users will see the change on next refresh.");
      await refreshRbac();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetDefaults = () => {
    if (!data) return;
    setMatrix(JSON.parse(JSON.stringify(data.defaults)));
    toast.info("Reverted to Clariva defaults — click Save to persist.");
  };

  const modulesByGroup = useMemo(() => {
    const g = { operate: [], intelligence: [], admin: [] };
    (data?.modules || []).forEach(m => { (g[m.group] ||= []).push(m); });
    return g;
  }, [data]);

  if (!data) return <PageShell title="Roles & Permissions" subtitle="Admin"><GlassCard className="p-6">Loading…</GlassCard></PageShell>;

  const roles = (data.roles || []).filter(r => !LOCKED_ROLES.has(r));

  return (
    <PageShell title="Roles & Permissions" subtitle="Admin · RBAC" right={
      <div className="flex gap-2">
        <Button variant="outline" onClick={resetDefaults} data-testid="reset-defaults-btn" className="gap-2">
          <ArrowCounterClockwise size={14} /> Reset to defaults
        </Button>
        <Button onClick={save} disabled={saving} data-testid="save-permissions-btn" className="gap-2">
          <FloppyDisk size={14} weight="fill" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    }>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <Alert className="mb-6" data-testid="rbac-explainer">
          <ShieldCheck className="w-4 h-4" />
          <AlertDescription>
            Each row is a role; each column is a module/screen. Check a box to let that role see that screen in their sidebar and access its data.
            <strong className="ml-1">Global Admin / Clariva Support</strong> always bypass these controls.
          </AlertDescription>
        </Alert>

        <GlassCard className="p-0 overflow-hidden" data-testid="rbac-matrix">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--surface-2)]/60 border-b border-[var(--border)]">
                  <th className="text-left font-medium px-4 py-3 sticky left-0 bg-[var(--surface-2)]/60 backdrop-blur z-10 min-w-[200px]">
                    <div className="flex items-center gap-1.5"><Users size={13} /> Role</div>
                  </th>
                  {(data.modules || []).map(m => (
                    <th key={m.key} className="px-2 py-3 font-medium text-[11px] text-center min-w-[80px] whitespace-nowrap" title={m.description}>
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="overline text-[9px] text-[var(--text-3)]">{m.group}</span>
                        <span>{m.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {roles.map(role => {
                  const set = new Set(matrix[role] || []);
                  const locked = LOCKED_ROLES.has(role);
                  return (
                    <tr key={role} className="border-b border-[var(--border)] hover:bg-[var(--surface-2)]/30" data-testid={`rbac-row-${role}`}>
                      <td className="px-4 py-3 sticky left-0 bg-[var(--bg)]/90 backdrop-blur z-10">
                        <div className="flex items-start gap-2">
                          {locked && <Lock size={12} className="mt-1 text-[var(--text-3)]" />}
                          <div>
                            <div className="font-medium">{ROLE_LABELS[role] || role}</div>
                            <div className="text-[10px] text-[var(--text-3)] leading-tight mt-0.5 max-w-[260px]">
                              {ROLE_DESCRIPTIONS[role] || "Custom role"}
                            </div>
                            <Badge variant="outline" className="mt-1 text-[9px] h-4">{role}</Badge>
                          </div>
                        </div>
                      </td>
                      {(data.modules || []).map(m => {
                        const checked = set.has(m.key);
                        const adminFloor = role === "admin" && (data?.admin_floor_modules || []).includes(m.key);
                        return (
                          <td key={m.key} className="text-center px-2 py-3" data-testid={`rbac-cell-${role}-${m.key}`}>
                            <Checkbox
                              checked={checked}
                              disabled={locked || adminFloor}
                              onCheckedChange={() => toggle(role, m.key)}
                              title={adminFloor ? "Required for Admin role — cannot be removed" : undefined}
                              data-testid={`rbac-toggle-${role}-${m.key}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </GlassCard>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {Object.entries(modulesByGroup).map(([grp, mods]) => (
            <GlassCard key={grp} className="p-4">
              <div className="overline text-xs mb-2">{grp}</div>
              <ul className="space-y-2">
                {mods.map(m => (
                  <li key={m.key} className="text-sm">
                    <div className="font-medium">{m.label}</div>
                    <div className="text-[11px] text-[var(--text-3)]">{m.description}</div>
                  </li>
                ))}
              </ul>
            </GlassCard>
          ))}
        </div>
      </motion.div>
    </PageShell>
  );
}
